import React, { useState, useContext } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  FormFeedback,
  Container,
  Row,
  Col,
} from 'reactstrap';

import agent from '../../../agent';
import AddressAutocomplete from '../../common/AddressAutocomplete';
import { EMPLOYER_NAME_MAX_LENGTH } from '../../../constants';

import { FaPencilAlt, FaPlus } from 'react-icons/fa';
import {
  OfficesContext,
  OfficesContextType,
} from '../../../context/ProfileContext';
import { UserContext, UserContextType } from '../../../context/UserContext';
import types from '../../../types/services-api';

const OfficeAddEditModal = (props: {
  office?: types.OfficeProfileViewType;
}) => {
  const { offices, upsertOffice, primaryOffice } = useContext(
    OfficesContext
  ) as OfficesContextType;
  const { user } = useContext(UserContext) as UserContextType;

  const [selectedAddress, setSelectedAddress] = useState({
    address: props.office ? props.office.address : '',
    address2: props.office ? props.office.address2 : '',
    city: props.office ? props.office.city : '',
    state: props.office ? props.office.state : '',
    zip: props.office ? props.office.zip : '',
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  function addOffice(e) {
    e.preventDefault();
    const body = transformFormValues(e.target.elements);
    agent.Employer.createOffice({
      body,
    })
      .then((res) => {
        upsertOffice(res.data);
        setModalOpen(false);
      })
      .catch((err) => {
        setErrorMessage(err.message);
      });
  }

  function updateOffice(e) {
    e.preventDefault();
    const body = transformFormValues(e.target.elements);
    if (!!props.office) {
      agent.Employer.updateOffice({
        officeId: props.office.id,
        body,
      })
        .then((res) => {
          upsertOffice(res.data);
          setModalOpen(false);
        })
        .catch((err) => {
          setErrorMessage(err.message);
        });
    }
  }

  function transformFormValues(formValues) {
    const { officeName, about, contact, website, email, phone } = formValues;
    return {
      name: officeName.value,
      about: about.value,
      address: selectedAddress.address,
      address2: selectedAddress.address2,
      city: selectedAddress.city,
      state: selectedAddress.state,
      zip: selectedAddress.zip,
      contact: contact.value,
      website: website.value,
      email: email.value,
      phone: phone.value,
    };
  }

  const officeNames = offices.reduce(
    (acc: string[], currentValue) => [...acc, currentValue.name],
    []
  );

  return (
    <>
      {!!props.office ? (
        <Button
          onClick={() => setModalOpen(true)}
          data-testid="addOffice"
          color="primary"
          outline
        >
          <FaPencilAlt title="edit office" />
        </Button>
      ) : (
        <Button
          onClick={() => setModalOpen(true)}
          data-testid="addOffice"
          color="primary"
          className="d-flex align-items-center m-3"
        >
          <FaPlus title="add office" />
        </Button>
      )}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalBody>
          <div className="d-flex justify-content-between border-bottom mb-3">
            <h5>Add/Edit Office</h5>
            <p className="text-end">
              <span className="text-danger">*</span> = required fields
            </p>
          </div>
          <Form
            id="addEditOfficeForm"
            onReset={() => setModalOpen(false)}
            onSubmit={!!props.office ? updateOffice : addOffice}
          >
            <Container fluid>
              <Row>
                <Col xs="12">
                  <FormGroup floating>
                    <Input
                      name="officeName"
                      id="officeName"
                      defaultValue={!!props.office ? props.office.name : ''}
                      maxLength={EMPLOYER_NAME_MAX_LENGTH}
                      required
                      onChange={(e) => {
                        const value: string = e.target.value;
                        if (officeNames.indexOf(value) > -1) {
                          e.target.setAttribute('aria-invalid', 'true');
                          e.target.classList.add('is-invalid');
                        } else {
                          if (e.target.classList.contains('is-invalid')) {
                            e.target.setAttribute('aria-invalid', 'false');
                            e.target.classList.remove('is-invalid');
                          }
                        }
                      }}
                    />
                    <Label for="officeName" required>
                      Office Name
                    </Label>
                    <FormFeedback invalid="true">
                      Please enter a unique office name
                    </FormFeedback>
                  </FormGroup>
                  <FormGroup floating>
                    <Input
                      type="text"
                      name="website"
                      id="website"
                      defaultValue={
                        !!props.office
                          ? props.office.website
                          : primaryOffice?.website
                      }
                    />
                    <Label for="website">Website</Label>
                  </FormGroup>
                  <AddressAutocomplete
                    selectedAddress={selectedAddress}
                    setSelectedAddress={setSelectedAddress}
                  />
                  <FormGroup floating>
                    <Input
                      type="text"
                      name="contact"
                      id="contact"
                      defaultValue={
                        !!props.office ? props.office.contact : user.name
                      }
                      required
                    />
                    <Label for="contact" required>
                      Office Contact Name
                    </Label>
                  </FormGroup>
                  <FormGroup floating>
                    <Input
                      type="text"
                      name="email"
                      id="email"
                      defaultValue={
                        !!props.office ? props.office.email : user.email
                      }
                      required
                    />
                    <Label for="contact" required>
                      Contact Email
                    </Label>
                  </FormGroup>
                  <FormGroup floating>
                    <Input
                      type="tel"
                      name="phone"
                      id="phone"
                      defaultValue={
                        !!props.office
                          ? props.office.phone
                          : primaryOffice?.phone
                      }
                    />
                    <Label for="contact">Contact Phone</Label>
                  </FormGroup>
                  <FormGroup floating>
                    <Input
                      type="textarea"
                      rows="10"
                      name="about"
                      id="about"
                      defaultValue={
                        !!props.office
                          ? props.office.about
                          : primaryOffice?.about
                      }
                      style={{ height: 'auto' }}
                    />
                    <Label for="about">About Your Office</Label>
                  </FormGroup>
                </Col>
              </Row>
            </Container>

            <Alert isOpen={!!errorMessage} color="danger">
              {errorMessage}
            </Alert>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button form="addEditOfficeForm" color="link" type="reset">
            Cancel
          </Button>
          <Button form="addEditOfficeForm" type="submit" color="primary">
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default OfficeAddEditModal;
